import { useCallback, useContext } from 'react';
import { getCookie } from 'js/utils/cookie.util';
import { filter, pick, prop, complement, isNil } from 'ramda';
import categoriesFromBIMapping from './categoriesFromBIMapping';
import { trackGamesPersonalizedGrid2 } from './trackingApi';
import { post, get } from 'js/utils/fetch.util';
import { useEffectOnce } from 'js/hooks/useEffectOnce';
import { useAppSelector, useDeviceSelector, useUserSelector } from 'js/store/hooks';
import { usePortletConfig, usePortletDispatch, usePortletSelector } from 'js/components/system/Portlet';
import { dynamicCategoriesFetchedAction } from './actions';
import { log } from 'log';
import { CategoryContext } from './context';

export const parsedMapCategoriesName = Object.keys(categoriesFromBIMapping).reduce((memo, key) => ({
    ...memo,
    [categoriesFromBIMapping[key].name]: {
        nameFromMapping: key,
        ...categoriesFromBIMapping[key]
    }
}), {});

export const getCategoriesToFetchFromBI = categories =>
    categories
        .filter(({ pgg2 = {}, name }) => pgg2.enabled && name in categoriesFromBIMapping)
        .map((category) => {
            const { name } = category;
            const { name: nameForBI = name } = categoriesFromBIMapping[name] || {};

            return { ...category, nameForBI };
        });

/* eslint-disable camelcase */
export const fetchCategoriesFromBI = ({
    clientPlatform: client_platform,
    deliveryPlatform: client_delivery_platform,
    pgg2: pgg2Config,
    clientType: client_type,
    userDetails,
    categories,
    country: country_code,
    reactPortletId,
    feedName,
    contentType: content_type,
    pasSessionToken: session_id
}) => {
    const { endpointUrl, casino: brand } = pgg2Config;

    if (!endpointUrl || categories.length === 0) {
        return Promise.resolve();
    }

    let request = {
        brand,
        client_type,
        client_platform,
        client_delivery_platform,
        content_type,
        session_id,
        ...country_code !== 'XX' && { country_code }
    };

    if (userDetails) {
        const { age, sex, username, userId: userid } = userDetails;

        request = { ...request, username, age, userid };
        request = sex ? { ...request, gender: sex.toLowerCase() } : request;
    }

    const affiliate = userDetails ? userDetails.advertiser : getCookie('banner_domainclick');

    request = affiliate ? { ...request, affiliate } : request;

    return Promise.all(categories.map((category) => {
        const { pgg2, nameForBI } = category;
        const { maxGames } = pgg2;

        const requestBody = {
            ...request,
            max_games: maxGames,
            category_names: [nameForBI]
        };

        const trackGamesPersonalizedGrid2Hof = isFallbackPresent =>
            trackGamesPersonalizedGrid2(reactPortletId, feedName, nameForBI, isFallbackPresent);
        const getErrorMsg = msg => `BI category '${nameForBI}' - ${msg}`;

        return new Promise((resolve, reject) => {
            const requestOptions = {
                referrerPolicy: 'no-referrer-when-downgrade',
                referrer: window.location.origin + window.location.pathname
            };
            const timeout = setTimeout(() => reject(getErrorMsg('timeout')), 2000);
            const url = `${endpointUrl}/categories`;

            post({ url, requestBody, requestOptions })
                .then((data) => {
                    if (data?.group === 'control') {
                        return reject(getErrorMsg('in "control" group')); // DEL `
                    }

                    trackGamesPersonalizedGrid2Hof(0);

                    resolve({ nameForBI, pgg2, ...data });
                })
                .catch(err => reject(getErrorMsg(err.message || err.error?.message)))
                .finally(() => clearTimeout(timeout));
        }).catch((e) => {
            trackGamesPersonalizedGrid2Hof(1);
            log('warn', e);
        });
    })).then(filter(Boolean));
};
/* eslint-enable camelcase */

export const trackLaunchGameFromBI = ({
    pgg2: pgg2Config,
    userName: username,
    gameCode: label,
    clientType: ctype,
    clientPlatform: cplat,
    tid: custval1,
    categoryName: custval2
}) => {
    const { endpointTrackingUrl, casino: brand } = pgg2Config;

    if (!endpointTrackingUrl) {
        return;
    }

    const requestParams = filter(complement(isNil), {
        username,
        label,
        brand,
        ctype,
        cplat,
        custval1,
        custval2,
        category: 'pgg2fl',
        action: 'game_open',
        mode: 'guest',
        custname1: 'tid',
        custname2: 'pgg_category',
        cb: Date.now() // cache breaker
    });

    const fullTrackingUrl = `${endpointTrackingUrl}?${new URLSearchParams(requestParams)}`;

    get(fullTrackingUrl);
};

export const useGetTrackLaunchGameFromBI = () => {
    const userName = useUserSelector(prop('userName'));
    const clientPlatform = useDeviceSelector(prop('clientPlatform'));
    const { pgg2, clientType } = useAppSelector(pick(['pgg2', 'clientType']));
    const { name: categoryName, tid, isBICategory } = useContext(CategoryContext);

    return useCallback((gameCode) => {
        if (!isBICategory) {
            return;
        }

        trackLaunchGameFromBI({
            pgg2,
            userName,
            gameCode,
            clientType,
            clientPlatform,
            tid,
            categoryName
        });
    }, [pgg2, userName, clientType, clientPlatform, tid, categoryName, isBICategory]);
};

export const useFetchCategoriesFromBI = () => {
    const { reactPortletId, contentType } = usePortletConfig();
    const { clientPlatform, deliveryPlatform } = useDeviceSelector(pick(['clientPlatform', 'deliveryPlatform']));
    const { pgg2, clientType } = useAppSelector(pick(['pgg2', 'clientType']));
    const { userDetails, country, pasSessionToken } = useUserSelector(
        pick(['userDetails', 'country', 'pasSessionToken']));
    const {
        feedName,
        feed: { categories: feedCategories = [] } = {}
    } = usePortletSelector(pick(['feedName', 'feed']));
    const categoriesToFetch = getCategoriesToFetchFromBI(feedCategories);
    const dispatch = usePortletDispatch();

    useEffectOnce(
        () => {
            if (categoriesToFetch.length === 0) {
                log('log', 'No BI Categories!');
                dispatch(dynamicCategoriesFetchedAction());
                return;
            }

            (async () => {
                let result;
                try {
                    result = await fetchCategoriesFromBI({
                        categories: categoriesToFetch,
                        feedName,
                        reactPortletId,
                        clientPlatform,
                        deliveryPlatform,
                        pgg2,
                        clientType,
                        userDetails,
                        country,
                        contentType,
                        pasSessionToken
                    });
                } finally {
                    dispatch(dynamicCategoriesFetchedAction(result));
                }
            })();
        },
        [
            categoriesToFetch,
            feedName,
            reactPortletId,
            clientPlatform,
            deliveryPlatform,
            pgg2,
            clientType,
            contentType,
            userDetails,
            country,
            pasSessionToken,
            dispatch
        ],
        () => feedCategories.length > 0
    );
};

export const parseDynamicCategories = (dynamicCategories, feedName) =>
    (feedCategories) => {
        const result = dynamicCategories.reduce((memo = {}, item) => ({
            ...memo,
            [item.nameForBI]: item.categories.map((category) => {
                /* eslint-disable camelcase */
                const { type, game_type, games: gameCodes, game_code } = category;
                const { nameForBI, pgg2, tid } = item;
                const name = `${type}-${game_type}`;
                const { roundIcon, nameFromMapping } = parsedMapCategoriesName[name] || {};
                const tKeyName = `${feedName.toLowerCase()}.${nameFromMapping}`;

                return {
                    cid: `${nameForBI}-dynamic-category-${name}`,
                    roundIcon,
                    isBICategory: true,
                    mostPlayedGameCode: game_code,
                    tid,
                    pgg2,
                    name,
                    gameCodes,
                    displayName: `playtech.mobile.game-categories.${tKeyName}.long`,
                    displayNameShort: `playtech.mobile.game-categories.${tKeyName}.short`
                };
                /* eslint-enable camelcase */
            })
        }), null);

        return !result
            ? feedCategories
            : feedCategories.reduce((memo, category) => {
                const { name: feedCategoryName } = category;
                const { name: BICategoryName } = categoriesFromBIMapping[feedCategoryName] || {};

                return memo.concat(result[BICategoryName] || result[feedCategoryName] || [category]);
            }, []);
    };

export const filterGamesByPGG2 = categories =>
    categories.filter(({ gameCodes, pgg2 = {}, isBICategory }) =>
        !pgg2.enabled
        || !isBICategory
        || gameCodes.length >= pgg2.minGames && gameCodes.length <= pgg2.maxGames);
